import React, { useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BASE_URL from "../config";
import { FixedSizeList as List } from "react-window";

// Helper to parse CSV (simple, no quoted commas)
function parseCSV(content) {
  const lines = content.split(/\r?\n/).filter(Boolean);
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
  const rows = lines.slice(1).map(line =>
    line.split(",").map(cell => cell.trim().replace(/^"|"$/g, ""))
  );
  return { headers, rows };
}

// Add a simple inline loader component
const InlineLoader = ({ message }) => (
  <span style={{ display: "inline-flex", alignItems: "center" }}>
    <span
      style={{
        width: 16,
        height: 16,
        border: "2px solid #fff",
        borderTop: "2px solid #007bff",
        borderRadius: "50%",
        display: "inline-block",
        marginRight: 8,
        animation: "spin 1s linear infinite",
      }}
    />
    {message || "Processing..."}
    <style>
      {`
        @keyframes spin {
          0% { transform: rotate(0deg);}
          100% { transform: rotate(360deg);}
        }
      `}
    </style>
  </span>
);

const EmailLeadExtractor = () => {
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [log, setLog] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [selectedEmailCol, setSelectedEmailCol] = useState("");
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showDropTableBtn, setShowDropTableBtn] = useState(false);
  const [dropTableLoading, setDropTableLoading] = useState(false);
  const [downloadCsvLoading, setDownloadCsvLoading] = useState(false);
  const [searchCol, setSearchCol] = useState(""); // Add this state
  const [selectedDumpLogs, setSelectedDumpLogs] = useState([]);

  // Helper to extract info from "Searched and dumped" logs
  function parseDumpLog(log) {
    // Example: Searched and dumped matched 9 records from tblHomeImprovementSiding.(took 0.11 secs | 19/05/2025, 14:51:39)
    const match = log.message.match(/Searched and dumped matched (\d+) records from ([\w.]+)/);
    if (match) {
      return {
        count: parseInt(match[1], 10),
        source: match[2],
        message: log.message,
        timestamp: log.timestamp,
      };
    }
    return null;
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setCsvHeaders([]);
    setCsvRows([]);
    setSelectedEmailCol("");
    setShowColumnSelector(false);
    setLog([]);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const { headers, rows } = parseCSV(content);
        setCsvHeaders(headers);
        setCsvRows(rows);
        setShowColumnSelector(true);
      };
      reader.readAsText(file);
    }
    console.log("[STEP] File selected:", file?.name);
  };

  const handleEmailColSelect = (col) => {
    setSelectedEmailCol(col);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first.");
      console.log("[ERROR] No file selected.");
      return;
    }
    if (!selectedEmailCol) {
      toast.error("Please select the email column.");
      return;
    }

    setStatus("Processing...");
    setError("");
    setIsProcessing(true);
    setLog([]);
    // setShowDropTableBtn(true); // Show the button immediately after upload starts
    const accumulatedRecords = [];
    console.log("[STEP] Upload started.");

    // Prepare only the selected column values (excluding header)
    const colIdx = csvHeaders.indexOf(selectedEmailCol);
    if (colIdx === -1) {
      setError("Selected column not found in CSV.");
      setIsProcessing(false);
      return;
    }
    const emails = csvRows
      .map(row => row[colIdx])
      .filter(email => !!email && email !== selectedEmailCol);

    if (emails.length === 0) {
      setError("No emails found in the selected column.");
      setIsProcessing(false);
      return;
    }

    try {
      // Create a Blob with the comma-separated emails and append to FormData as 'file'
      const emailBlob = new Blob([emails.join(",")], { type: "text/plain" });
      const formData = new FormData();
      formData.append("file", emailBlob, "emails.txt");

      console.log("[STEP] Emails to be sent to backend (as file):", emails.slice(0, 10).join(",") + (emails.length > 10 ? "..." : ""));

      const response = await fetch(`${BASE_URL}api/email-extractor/upload-csv`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        console.log("[ERROR] Backend returned error status.");
        throw new Error("Failed to process file");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let csvDownloaded = false;
      let allLogs = []; // <-- Collect all logs

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop();

        lines.forEach((line) => {
          if (line.trim()) {
            try {
              const parsedLine = JSON.parse(line);
              console.log("[STEP] Received chunk from backend:", parsedLine);

              if (parsedLine.logs) {
                // Append all logs to allLogs array (do not set the source name in UI)
                allLogs = [
                  ...allLogs,
                  ...parsedLine.logs.map((logEntry) => ({
                    message: logEntry.message,
                    timestamp: logEntry.timestamp || "Timestamp Missing",
                  })),
                ];
                setLog([...allLogs]);
              }

              if (parsedLine.records) {
                accumulatedRecords.push(...parsedLine.records);
                console.log("[STEP] Records accumulated:", accumulatedRecords.length);
              }
              // CSV download logic moved below to ensure it only happens once and only after all logs
              if (parsedLine.final && parsedLine.csvData && !csvDownloaded) {
                // Wait for all logs to be processed before downloading
                setTimeout(() => {
                  if (!csvDownloaded) {
                    console.log("[STEP] Downloading CSV file...");
                    downloadCSV(parsedLine.csvData, "fetched_data.csv");
                    csvDownloaded = true;

                    // Only add final logs if they come from the API (do not generate here)
                    // Just update status and UI
                    setStatus("Processing complete.");
                    toast.success("Emails processed successfully!");
                    setIsProcessing(false);
                    setShowDropTableBtn(true);
                    console.log("[STEP] CSV download triggered and process finished.");
                  }
                }, 100); // slight delay to ensure all logs are rendered before download
              }
              if (parsedLine.logs && parsedLine.logs.some(l => l.message && l.message.includes("error"))) {
                setIsProcessing(false);
              }
            } catch (err) {
              console.error("[ERROR] Error parsing JSON line:", err.message);
            }
          }
        });
      }
    } catch (err) {
      setError("Error: " + err.message);
      setStatus("");
      toast.error("Failed to process emails.");
      console.error("[ERROR] Failed to process emails:", err.message);
      setIsProcessing(false);
      console.log("[STEP] Loader hidden, process finished.");
    }
  };

  const handleDropOutputTable = async () => {
    try {
      setDropTableLoading(true);
      setStatus("Deleting temporary tables...");
      const response = await fetch(`${BASE_URL}api/email-extractor/drop-output-table`, {
        method: "POST"
      });
      if (response.ok) {
        toast.success("Temporary tables deleted successfully.");
        setShowDropTableBtn(false);
        setStatus("");
        setDropTableLoading(false);
        window.location.reload();
      } else {
        toast.error("Failed to delete temporary tables.");
        setStatus("");
        setDropTableLoading(false);
      }
    } catch (err) {
      toast.error("Error deleting temporary tables.");
      setStatus("");
      setDropTableLoading(false);
    }
  };

  const handleDownloadOutputTable = async () => {
    try {
      setDownloadCsvLoading(true);
      setStatus("Downloading output table CSV...");
      console.log("[STEP] Download Output Table button clicked.");
      // Make sure the URL is correct and points to your Node.js backend, not directly to PHP
      const response = await fetch(`${BASE_URL}api/email-extractor/download-output-table`, {
        method: "GET"
      });
      if (response.ok) {
        // Ensure the CSV is downloaded as a file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "fetched_data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Output table CSV downloaded.");
        setStatus("");
        console.log("[STEP] Output table CSV file downloaded.");
      } else {
        toast.error("Failed to download output table CSV.");
        setStatus("");
        console.error("[ERROR] Failed to download output table CSV. Status:", response.status);
      }
      setDownloadCsvLoading(false);
    } catch (err) {
      toast.error("Error downloading output table CSV.");
      setStatus("");
      setDownloadCsvLoading(false);
      console.error("[ERROR] Error downloading output table CSV:", err.message);
    }
  };

  const downloadCSV = (csvContent, filename) => {
    try {
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log("[STEP] CSV file created and download triggered.");
    } catch (e) {
      console.error("[ERROR] CSV download failed:", e.message);
    }
  };

  // Show drop table button if there are logs displayed
  React.useEffect(() => {
    if (log.length > 0) {
      setShowDropTableBtn(true);
    }
  }, [log]);

  return (
    <div className="container mt-4">
      <h2>Upload Email CSV</h2>
      <form>
        <div className="mb-3">
          <label htmlFor="csvFile" className="form-label">
            Choose CSV file (with email column):
          </label>
          <input
            type="file"
            id="csvFile"
            name="csvFile"
            accept=".csv"
            className="form-control"
            onChange={handleFileChange}
            disabled={isProcessing}
          />
        </div>

        {showColumnSelector && csvHeaders.length > 0 && (
          <div className="mb-3">
            <label className="form-label">Select the email column:</label>
            <select
              className="form-select"
              value={selectedEmailCol}
              onChange={e => handleEmailColSelect(e.target.value)}
              disabled={isProcessing}
              style={{ maxWidth: 300 }}
            >
              <option value="">-- Select column --</option>
              {csvHeaders.map(header => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary"
          onClick={handleFileUpload}
          disabled={!selectedFile || isProcessing || !selectedEmailCol}
        >
          {isProcessing ? <InlineLoader message={status} /> : "Upload and Process"}
        </button>
        {/* Show drop table button if logs are displayed */}
        {showDropTableBtn && (
          <div style={{ display: "inline-flex", gap: "12px", marginLeft: "12px" }}>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleDownloadOutputTable}
              disabled={downloadCsvLoading}
            >
              {downloadCsvLoading ? "Downloading..." : "Download CSV File"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDropOutputTable}
              disabled={dropTableLoading}
            >
              {dropTableLoading ? "Deleting..." : "Delete Temporary Tables"}
            </button>
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}
      </form>

      <div className="row">
        <div className="col-md-8">
          <div className="log mt-4 p-3 bg-light rounded">
            <List
              height={400}
              itemCount={log.length}
              itemSize={50}
              width="100%"
            >
              {({ index, style }) => (
                <div style={style} className="mb-2">
                  <span dangerouslySetInnerHTML={{ __html: log[index].message }}></span>
                  {!log[index].message.includes("timestamp") && (
                    <span className="timestamp">({log[index].timestamp})</span>
                  )}
                </div>
              )}
            </List>
          </div>
        </div>
        <div className="col-md-4">
          {/* Searched and dumped logs as checkboxes */}
          <div className="card mb-3">
            <div className="card-header">
              <b>Searched and dumped logs</b>
            </div>
            <div className="card-body" style={{ maxHeight: 300, overflowY: "auto" }}>
              {log
                .map((l, idx) => ({ ...parseDumpLog(l), idx }))
                .filter(l => l && l.message && l.message.startsWith("Searched and dumped"))
                .map(l => (
                  <div key={l.idx} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`dump-log-${l.idx}`}
                      checked={selectedDumpLogs.includes(l.idx)}
                      onChange={e => {
                        setSelectedDumpLogs(prev =>
                          e.target.checked
                            ? [...prev, l.idx]
                            : prev.filter(i => i !== l.idx)
                        );
                      }}
                    />
                    <label className="form-check-label" htmlFor={`dump-log-${l.idx}`}>
                      {l.count} from <b>{l.source}</b>
                    </label>
                  </div>
                ))}
            </div>
          </div>
          {/* Summary of selected logs */}
          {selectedDumpLogs.length > 0 && (
            <div className="card">
              <div className="card-header">
                <b>Selected Summary</b>
              </div>
              <div className="card-body">
                {(() => {
                  const selected = log
                    .map((l, idx) => ({ ...parseDumpLog(l), idx }))
                    .filter(l => l && selectedDumpLogs.includes(l.idx));
                  const total = selected.reduce((sum, l) => sum + l.count, 0);
                  const sources = selected.map(l => l.source);
                  return (
                    <div>
                      <b>{total} records from</b>
                      <ul>
                        {sources.map(src => (
                          <li key={src}>{src}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailLeadExtractor;
